import {
    PrivilegedRouteMap,
    PrivilegedRouteObject,
} from '@thoughtspot/blink-context';
import { isSpreadsheetsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { Navigate, useParams } from 'react-router-dom';
import { AdhocAnswerLayoutProvider } from '/@contexts/adhoc-answer-layout-context';
import { SavedAnswerLayoutProvider } from '/@contexts/saved-answer-layout-context';
import { BlinkV2MetricsContextStates } from '/@services/system/blink-v2-metrics-context-states';
import { LeftPanelItem } from '/@utils/common-enum';
import { PERF_EVENT, PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { SearchDataLayout } from '../pages/search-pages/search-data-layout';
import { PrivilegeList } from './privilege-list';

export const EMBED_ROUTE = '/embed';

const AdhocAnswer = lazy(() => import('../pages/search-pages/adhoc-answer'));
const SavedAnswer = lazy(() => import('../pages/search-pages/saved-answer'));
const EurekaPage = lazy(() => import('../pages/eureka/eureka-page'));
const BenchmarkPage = lazy(() =>
    import('../pages/spotter-benchmark/spotter-benchmark-page'),
);

const EditACopyPage = lazy(() => import('../pages/search-pages/edit-a-copy'));

const SpreadsheetPage = lazy(() =>
    import('../pages/search-pages/spreadsheet/spreadsheet-page'),
);

const AutoAnswerPage = lazy(() =>
    import('../pages/search-pages/auto-answer-page'),
);

const EmbedSamlComplete = lazy(() =>
    import('../pages/embed-saml-complete/embed-saml-complete'),
);

const PrintView = lazy(() =>
    import('../components/print/print-view.container'),
);

const Liveboards = lazy(() =>
    import('../components/home/liveboards/liveboards.container'),
);

const Homepage = lazy(() => import('../pages/homepage/home-page'));

const Answers = lazy(() =>
    import('../components/home/answers/answers.container'),
);

const Apps = lazy(() => import('../components/home/apps/apps.container'));

const isLazyPinboardPageEnabled = () =>
    (window as any)?.blink?.performanceFlags?.isLazyPinboardPageEnabled ===
    'true';

const PinboardPage = lazy(() => {
    setPerfLog(PERF_KEYS.PINBOARD_PAGE_LAZY_IMPORT, PERF_EVENT.START);
    if (isLazyPinboardPageEnabled()) {
        return import('../pages/pinboard-pages/lazy-pinboard-page').then(
            module => {
                setPerfLog(
                    PERF_KEYS.PINBOARD_PAGE_LAZY_IMPORT,
                    PERF_EVENT.COMPLETE,
                );
                return module;
            },
        );
    }
    return import('../pages/pinboard-pages/pinboard-page').then(module => {
        setPerfLog(PERF_KEYS.PINBOARD_PAGE_LAZY_IMPORT, PERF_EVENT.COMPLETE);
        return module;
    });
});

const PinboardSchedulePage = lazy(() =>
    import('../pages/pinboard-pages/pinboard-schedule'),
);

const PinboardEmbedPage = lazy(() => {
    setPerfLog(PERF_KEYS.PINBOARD_EMBED_PAGE_LAZY_IMPORT, PERF_EVENT.START);
    if (isLazyPinboardPageEnabled()) {
        return import('../pages/pinboard-pages/lazy-pinboard-embed').then(
            module => {
                setPerfLog(
                    PERF_KEYS.PINBOARD_EMBED_PAGE_LAZY_IMPORT,
                    PERF_EVENT.COMPLETE,
                );
                return module;
            },
        );
    }
    return import('../pages/pinboard-pages/pinboard-embed').then(module => {
        setPerfLog(
            PERF_KEYS.PINBOARD_EMBED_PAGE_LAZY_IMPORT,
            PERF_EVENT.COMPLETE,
        );
        return module;
    });
});

const VizEmbedPage = lazy(() => {
    setPerfLog(PERF_KEYS.VIZ_EMBED_PAGE_LAZY_IMPORT, PERF_EVENT.START);
    if (isLazyPinboardPageEnabled()) {
        return import('../pages/pinboard-pages/lazy-viz-embed').then(module => {
            setPerfLog(
                PERF_KEYS.VIZ_EMBED_PAGE_LAZY_IMPORT,
                PERF_EVENT.COMPLETE,
            );
            return module;
        });
    }
    return import('../pages/pinboard-pages/viz-embed').then(module => {
        setPerfLog(PERF_KEYS.VIZ_EMBED_PAGE_LAZY_IMPORT, PERF_EVENT.COMPLETE);
        return module;
    });
});

const SearchBarEmbed = lazy(() =>
    import('../pages/search-pages/search-bar-embed'),
);

const EmbedPage = lazy(() => import('../pages/embed/embed-page'));

const RequestAccessPage = lazy(() =>
    import('../pages/request-access/request-access-object-no-access-page'),
);

const SpotIQListPage = lazy(() =>
    import('../components/home/spotiq-analysis/spotiq-analysis.container'),
);

const SpotIQInsightPage = lazy(() =>
    import('../pages/spotiq/analysis-result-page'),
);

const ConvAssistPage = lazy(() =>
    import('../pages/conv-assist/conv-assist-page'),
);

// Read-only recipient view for a shared Spotter conversation
// (#/conv-assist/s/:shareId). Lazy-loaded like all page components.
const ConvAssistSharedPage = lazy(() =>
    import('../pages/conv-assist/conv-assist-shared-page'),
);

const ConvexPage = lazy(() => import('../pages/convex/convex-page'));

const CaptainPage = lazy(() => import('../pages/captain/captain-page'));

const SpotterPage = lazy(() => import('../pages/spotter/spotter-page'));
const RedirectToSchedules = () => {
    const { pinboardId } = useParams();
    return <Navigate to={`/schedules/${pinboardId}`} replace />;
};

const ConvAssistAnswerPage = React.lazy(() =>
    import('../pages/conv-assist-answer/conv-assist-answer-page'),
);

export const insightsRoutes: PrivilegedRouteMap = {
    // Answer (AdhocAnswer) and Spreadsheet share SearchDataLayout as a nested
    // layout route so that the layout component stays mounted when navigating
    // between the two views. This preserves any state or context held in the
    // layout across the tab switch, avoiding a full unmount/remount that would
    // occur with sibling flat routes.
    //
    // The path uses /* so that the URL existence check in authenticated-app-view.tsx
    // matches both child URLs (/answer and /answer/spreadsheet) via a single ROUTES
    // entry. Without /*, matchPath with end:true would only match the exact parent
    // path and the spreadsheet URL would trigger a redirect to home.
    //
    // addEmbedRoutes cannot propagate nested children into embed variants, so the
    // embed variant (Embed_Answer) is defined manually instead of going through
    // the embedRouteList. AdhocAnswer is embeddable via Embed_Answer. Spreadsheet
    // is intentionally excluded from the embed children because it is not yet
    // supported in embedded contexts.
    AnswerLayout: {
        path: '/insights?/answer/*',
        element: (
            <AdhocAnswerLayoutProvider>
                <SearchDataLayout />
            </AdhocAnswerLayoutProvider>
        ),
        pageContext: BlinkV2MetricsContextStates.ANSWER_PAGE,
        title: 'EmbedDev.Playground.FeatureType.queryBuilder',
        isOverlayLeftPanel: true,
        // Hiding the entry points does not stop a bookmark or shared link.
        privileges: [PrivilegeList.CanCreateAnswers],
        children: {
            // Keys must be globally unique across the entire router tree
            // (routing.util.ts uses the map key directly as the React Router
            // route id). "AdhocAnswer" and "AnswerSpreadsheet" avoid the
            // collision with SavedAnswerLayout's "SavedAnswer"/"Spreadsheet"
            // children.
            AdhocAnswer: {
                path: '',
                element: <AdhocAnswer />,
                pageContext: BlinkV2MetricsContextStates.ANSWER_PAGE,
                isOverlayLeftPanel: true,
            },
            AnswerSpreadsheet: {
                path: 'spreadsheet',
                element: <SpreadsheetPage />,
                pageContext: BlinkV2MetricsContextStates.ANSWER_PAGE,
                isOverlayLeftPanel: true,
            },
        },
    },
    // Embed variant renders AdhocAnswer directly without SearchDataLayout.
    // The new layout is a standalone-only feature; embedded consumers get the
    // standard answer view with no layout chrome.
    Embed_Answer: {
        path: `${EMBED_ROUTE}/insights?/answer`,
        element: <AdhocAnswer />,
        pageContext: BlinkV2MetricsContextStates.ANSWER_PAGE,
        isOverlayLeftPanel: true,
    },
    AutoAnswer: {
        path: '/answer/create/auto/:dataSourceId',
        element: <AutoAnswerPage />,
        pageContext: BlinkV2MetricsContextStates.AUTO_ANSWER,
        isOverlayLeftPanel: true,
        privileges: [PrivilegeList.CanCreateAnswers],
    },
    EditACopy: {
        path: '/insights?/answer/edit/:editACopySessionKey',
        element: <EditACopyPage />,
        pageContext: BlinkV2MetricsContextStates.EDIT_A_COPY,
        isOverlayLeftPanel: true,
        // Edit-a-copy persists a new Answer; Make a copy is already gated.
        privileges: [PrivilegeList.CanCreateAnswers],
    },
    EurekaPage: {
        path: '/insights?/eureka',
        element: <EurekaPage />,
        pageContext: BlinkV2MetricsContextStates.EUREKA,
        isOverlayLeftPanel: true,
    },
    SpotterBenchmark: {
        path: '/insights?/benchmark/*',
        element: <BenchmarkPage />,
        privileges: [PrivilegeList.CanAccessSpotterBenchmark],
        pageContext: BlinkV2MetricsContextStates.SPOTTER_BENCHMARK,
        isOverlayLeftPanel: true,
    },
    Default: {
        element: <Homepage id={LeftPanelItem.HOME} />,
        enableSearch: true,
        pageContext: BlinkV2MetricsContextStates.HOME_PAGE,
    },
    Home: {
        path: '*',
        element: <Homepage id={LeftPanelItem.HOME} />,
        enableSearch: true,
        pageContext: BlinkV2MetricsContextStates.HOME_PAGE,
    },
    StratusHome: {
        path: '/home',
        element: <Homepage id={LeftPanelItem.HOME} />,
        enableSearch: true,
        pageContext: BlinkV2MetricsContextStates.HOME_PAGE,
    },
    Pinboard: {
        path: '/insights?/pinboard/:pinboardId/:vizId?',
        element: <PinboardPage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_PAGE,
        isOverlayLeftPanel: true,
    },
    HomeLiveboards: {
        id: 'liveboards',
        path: '/insights?/home?/liveboards',
        element: <Homepage id={LeftPanelItem.LIVEBOARDS} />,
        pageContext: BlinkV2MetricsContextStates.PINBOARDS_LIST,
        title: 'homeleftpanel.liveboards',
    },
    Collections: {
        id: LeftPanelItem.COLLECTIONS,
        path: '/insights?/collections/:collectionId?',
        element: <Homepage id={LeftPanelItem.COLLECTIONS} />,
        pageContext: BlinkV2MetricsContextStates.COLLECTIONS,
        title: 'homeleftpanel.collections',
    },
    Pinboards: {
        path: '/insights?/pinboards',
        element: <Liveboards />,
        pageContext: BlinkV2MetricsContextStates.PINBOARDS_LIST,
        title: 'homeleftpanel.liveboards',
        isOverlayLeftPanel: true,
    },
    Liveboards: {
        path: '/insights?/liveboards',
        element: <Liveboards />,
        pageContext: BlinkV2MetricsContextStates.PINBOARDS_LIST,
        title: 'homeleftpanel.liveboards',
        isOverlayLeftPanel: true,
    },
    Apps: {
        path: '/insights?/apps',
        element: <Apps />,
        privileges: [PrivilegeList.CanAccessApps],
        pageContext: BlinkV2MetricsContextStates.APPS_LIST,
        title: 'homeleftpanel.apps',
        isOverlayLeftPanel: true,
    },
    HomeApps: {
        id: LeftPanelItem.APPS,
        path: '/insights?/home/apps',
        element: <Homepage id={LeftPanelItem.APPS} />,
        privileges: [PrivilegeList.CanAccessApps],
        pageContext: BlinkV2MetricsContextStates.APPS_LIST,
        title: 'homeleftpanel.apps',
    },
    SpotIQAnalySis: {
        id: LeftPanelItem.SPOTIQ_ANALYSIS,
        path: '/insights?/home?/spotiq-analysis',
        element: <Homepage id={LeftPanelItem.SPOTIQ_ANALYSIS} />,
        privileges: [PrivilegeList.CanAccessA3],
        pageContext: BlinkV2MetricsContextStates.SPOTIQ_ANALYSIS,
        title: 'a3.VIEW_SPOTIQ_ANALYSIS',
    },
    SpotIQAnalysis: {
        path: '/insights/analyse',
        element: <Homepage id={LeftPanelItem.SPOTIQ_ANALYSIS} />,
        privileges: [PrivilegeList.CanAccessA3],
        pageContext: BlinkV2MetricsContextStates.SPOTIQ_ANALYSIS,
        title: 'a3.VIEW_SPOTIQ_ANALYSIS',
    },
    SpotIQAnalysisV1Path: {
        path: '/insights/results',
        element: <Homepage id={LeftPanelItem.SPOTIQ_ANALYSIS} />,
        privileges: [PrivilegeList.CanAccessA3],
        pageContext: BlinkV2MetricsContextStates.SPOTIQ_ANALYSIS,
        title: 'a3.VIEW_SPOTIQ_ANALYSIS',
    },
    MonitorAlerts: {
        id: LeftPanelItem.MONITOR_ALERTS,
        path: '/insights?/home?/monitor-alerts',
        element: <Homepage id={LeftPanelItem.MONITOR_ALERTS} />,
        privileges: [PrivilegeList.CanAccessMonitor],
        pageContext: BlinkV2MetricsContextStates.MONITOR_RULE_LIST,
        title: 'homeleftpanel.monitorsubscription',
    },
    Monitor: {
        path: '/monitor',
        element: <Homepage id={LeftPanelItem.MONITOR_ALERTS} />,
        privileges: [PrivilegeList.CanAccessMonitor],
        pageContext: BlinkV2MetricsContextStates.MONITOR_RULE_LIST,
        title: 'homeleftpanel.monitorsubscription',
    },
    Answers: {
        path: '/insights?/answers',
        element: <Answers />,
        pageContext: BlinkV2MetricsContextStates.ANSWERS_LIST,
        title: 'homeleftpanel.answers',
        isOverlayLeftPanel: true,
    },
    HomeAnswers: {
        id: LeftPanelItem.ANSWERS,
        path: '/insights?/home/answers',
        element: <Homepage id={LeftPanelItem.ANSWERS} />,
        pageContext: BlinkV2MetricsContextStates.ANSWERS_LIST,
        title: 'homeleftpanel.answers',
    },
    HomeFavourites: {
        id: LeftPanelItem.FAVOURITES,
        path: '/insights?/home?/favourites',
        element: <Homepage id={LeftPanelItem.FAVOURITES} />,
        pageContext: BlinkV2MetricsContextStates.FAVORITE,
        title: 'homeleftpanel.home.favourites',
    },
    liveboardSchedule: {
        id: LeftPanelItem.LIVEBOARD_SCHEDULES,
        path: '/insights?/home/liveboard-schedules',
        privileges: [PrivilegeList.CanAccessLiveboardSchedulesOnHome],
        element: <Homepage id={LeftPanelItem.LIVEBOARD_SCHEDULES} />,
        pageContext: BlinkV2MetricsContextStates.LIVEBOARD_SCHEDULES,
        title: 'homeleftpanel.liveboardschedules',
    },
    CreatedByMe: {
        id: LeftPanelItem.CREATED_BY_ME,
        path: '/insights?/home?/created-by-me',
        element: <Homepage id={LeftPanelItem.CREATED_BY_ME} />,
        pageContext: BlinkV2MetricsContextStates.CREATED_BY_ME,
        title: 'homeleftpanel.createdbyme',
    },
    PinboardEmbed: {
        path: '/insights?/viz/:pinboardId',
        element: <PinboardEmbedPage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_CARD_ANSWER,
        isOverlayLeftPanel: true,
    },
    PinboardSchedule: {
        path: '/insights?/schedules/:pinboardId',
        privileges: [PrivilegeList.CanAccessLiveboardSchedules],
        element: <PinboardSchedulePage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_SCHEDULE,
        isOverlayLeftPanel: true,
    },
    // adding a schedule route to handle the request from blink-v1 components.
    PinboardScheduleForBlink: {
        path: '/insights?/schedule/:pinboardId/:scheduleId',
        element: <RedirectToSchedules />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_SCHEDULE,
        isOverlayLeftPanel: true,
    },
    PrintView: {
        path: '/insights?/print',
        element: <PrintView />,
        disableTopNav: true,
        pageContext: BlinkV2MetricsContextStates.PRINT_PAGE,
        isOverlayLeftPanel: true,
    },
    RequestAccess: {
        path: '/requestaccess/:objectType/:objectId',
        element: <RequestAccessPage />,
        pageContext: BlinkV2MetricsContextStates.REQUEST_ACCESS,
        isOverlayLeftPanel: true,
    },
    SAMLComplete: {
        path: '/saml-complete',
        element: <EmbedSamlComplete />,
        pageContext: BlinkV2MetricsContextStates.SAML_COMPLETE,
        isOverlayLeftPanel: true,
    },
    // SavedAnswer and Spreadsheet share SearchDataLayout as a nested layout route so
    // that the layout component stays mounted when navigating between the two views.
    // This preserves any state or context held in the layout across the tab switch,
    // avoiding a full unmount/remount that would occur with sibling flat routes.
    //
    // The path uses /* so that the URL existence check in authenticated-app-view.tsx
    // matches both child URLs (/saved-answer/:answerId and /saved-answer/:answerId/spreadsheet)
    // via a single ROUTES entry. Without /*, matchPath with end:true would only match
    // the exact parent path and the spreadsheet URL would trigger a redirect to home.
    //
    // addEmbedRoutes cannot propagate nested children into embed variants, so the embed
    // layout (Embed_SavedAnswer) is defined manually here instead of going through
    // the embedRouteList. SavedAnswer is embeddable via Embed_SavedAnswer. Spreadsheet
    // is intentionally excluded from the embed children because it is not yet supported
    // in embedded contexts.
    SavedAnswerLayout: {
        path: '/insights?/saved-answer/:answerId/*',
        element: (
            <SavedAnswerLayoutProvider>
                <SearchDataLayout />
            </SavedAnswerLayoutProvider>
        ),
        pageContext: BlinkV2MetricsContextStates.SAVED_ANSWER,
        isOverlayLeftPanel: true,
        children: {
            SavedAnswer: {
                path: '',
                element: (
                    <SavedAnswer key={BlinkV2MetricsContextStates.ANSWER} />
                ),
                pageContext: BlinkV2MetricsContextStates.SAVED_ANSWER,
                isOverlayLeftPanel: true,
            },
            Spreadsheet: {
                path: 'spreadsheet',
                element: <SpreadsheetPage />,
                pageContext: BlinkV2MetricsContextStates.SAVED_ANSWER,
                isOverlayLeftPanel: true,
            },
        },
    },
    // Embed variant renders SavedAnswer directly without SearchDataLayout.
    // The new layout is a standalone-only feature; embedded consumers get the
    // standard answer view with no layout chrome.
    Embed_SavedAnswer: {
        path: `${EMBED_ROUTE}/insights?/saved-answer/:answerId`,
        element: <SavedAnswer key={BlinkV2MetricsContextStates.ANSWER} />,
        pageContext: BlinkV2MetricsContextStates.SAVED_ANSWER,
        isOverlayLeftPanel: true,
    },
    SearchBarEmbed: {
        path: '/search-bar-embed',
        element: <SearchBarEmbed />,
        pageContext: BlinkV2MetricsContextStates.SEARCH_BAR_EMBED,
        isOverlayLeftPanel: true,
    },
    ConvAssistAnswer: {
        path: '/conv-assist-answer',
        element: <ConvAssistAnswerPage />,
        pageContext: BlinkV2MetricsContextStates.CONV_ASSIST_ANSWER,
        isOverlayLeftPanel: true,
    },
    SpotIQList: {
        path: '/insights',
        element: <SpotIQListPage />,
        pageContext: BlinkV2MetricsContextStates.SPOTIQ_ANALYSIS,
        privileges: [PrivilegeList.CanAccessA3],
        isOverlayLeftPanel: true,
    },
    SpotIQ: {
        path: '/insights?/insight/:analysisResultId',
        element: <SpotIQInsightPage />,
        pageContext: BlinkV2MetricsContextStates.SPOTIQ_ANALYSIS,
        privileges: [PrivilegeList.CanAccessA3],
        isOverlayLeftPanel: true,
    },
    Tab: {
        path: '/insights?/pinboard/:pinboardId/tab/:tabId/:vizId?',
        element: <PinboardPage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_PAGE,
        isOverlayLeftPanel: true,
    },
    TabEmbed: {
        path: '/insights?/viz/:pinboardId/tab/:tabId/:vizId?',
        element: <PinboardEmbedPage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_PAGE,
        disableTopNav: true,
        isOverlayLeftPanel: true,
    },
    View: {
        path: '/insights?/view/:answerId',
        element: (
            <SavedAnswer isSavedView key={BlinkV2MetricsContextStates.VIEW} />
        ),
        pageContext: BlinkV2MetricsContextStates.VIEW,
        isOverlayLeftPanel: true,
    },
    VizEmbed: {
        path: '/insights?/viz/:pinboardId/:vizId',
        element: <VizEmbedPage />,
        pageContext: BlinkV2MetricsContextStates.PINBOARD_CARD_ANSWER,
        disableTopNav: true,
        isOverlayLeftPanel: true,
    },
    ConvAssist: {
        path: '/insights?/conv-assist',
        element: <ConvAssistPage />,
        pageContext: BlinkV2MetricsContextStates.CONV_ASSIST,
        isOverlayLeftPanel: true,
    },
    ConvAssistShared: {
        path: '/insights?/conv-assist/s/:shareId',
        element: <ConvAssistSharedPage />,
        pageContext: BlinkV2MetricsContextStates.CONV_ASSIST,
        isOverlayLeftPanel: true,
    },
    Convex: {
        path: '/insights?/convex',
        element: <ConvexPage />,
        pageContext: BlinkV2MetricsContextStates.CONVEX,
        isOverlayLeftPanel: true,
    },
    CaptainPage: {
        path: '/insights?/captain/*',
        element: <CaptainPage />,
        pageContext: BlinkV2MetricsContextStates.CAPTAIN,
        isOverlayLeftPanel: true,
    },
    Spotter: {
        // Wildcard: the spotter MFE owns every path below this mount and does
        // its own routing (js/ts-apps/spotter/src/routes.tsx), the same way
        // data-model and edge do. Chrome (isOverlayLeftPanel / pageContext /
        // disableTopNav) is resolved per route ENTRY, so every spotter subpath
        // shares the config below.
        //
        // ADDING A ROUTE UNDER /insights/spotter/... FROM ANOTHER MFE?
        // It works — react-router v6 ranks by specificity, not declaration
        // order, so a static `/insights?/spotter/abc` entry beats this splat
        // regardless of where you put it. Three things will bite you:
        //
        //  1. SILENT SHADOWING. Your entry also wins over any route the
        //     spotter MFE declares internally for the same segment, now or
        //     later. Theirs becomes dead code that never matches, with no
        //     error — and the cause lives in a different repo from the
        //     symptom. Check js/ts-apps/spotter/src/routes.tsx first, and tell
        //     that team.
        //  2. DECLARE THE SUBTREE, NOT THE SEGMENT. `.../abc` catches exactly
        //     `/insights/spotter/abc`; `/insights/spotter/abc/deeper` falls
        //     back here and renders spotter's catch-all (the chat page), which
        //     looks like "my feature is missing" rather than a routing miss.
        //     Use `.../abc/*` if you own a subtree.
        //  3. Same applies to the embed twin: Spotter is in embedRouteList
        //     below, so addEmbedRoutes copies this path verbatim and
        //     /embed/insights/spotter/* ranks identically.
        //
        // If the page is conceptually part of spotter, prefer having spotter
        // host it via a `helpers` render prop over competing for its URL space.
        path: '/insights?/spotter/*',
        element: <SpotterPage />,
        pageContext: BlinkV2MetricsContextStates.SPOTTER,
        isOverlayLeftPanel: true,
    },
};

// There is currently no API to hide the top nav from within SearchDataLayout
// (or any child component).
//
// The only supported mechanism is the `disableTopNav` field on route config
// objects, which authenticated-app-view reads via ROUTES[match.id].disableTopNav
// inside a useMemo that re-runs on every route change.
//
// The problem: the insightsRoutes object is a static literal evaluated at module
// load time — before the postMessage/preauth handshake that populates session
// info. Calling isSpreadsheetsEnabled() directly in the object literal always
// returns false (session info not yet set).
//
// Workaround: use Object.defineProperty to make disableTopNav a lazy getter on
// the route objects. Because ROUTES spreads route references (not deep clones),
// ROUTES['AnswerLayout'] is the same object instance, so the getter is invoked
// on each useMemo evaluation — at which point session info is already loaded.
//
// If a proper imperative nav-hiding API becomes available (e.g. a dedicated
// field in SparkflowContextOptions or a navService.hideNavBar() method), this
// workaround should be replaced with that mechanism.
Object.defineProperty(insightsRoutes.AnswerLayout, 'disableTopNav', {
    get: () => isSpreadsheetsEnabled(),
    enumerable: true,
    configurable: true,
});
Object.defineProperty(insightsRoutes.SavedAnswerLayout, 'disableTopNav', {
    get: () => isSpreadsheetsEnabled(),
    enumerable: true,
    configurable: true,
});

/**
 * Enhance the base routes object with the embeddable routes
 * @param sourceRoutes The base routes of the app
 * @param embedRoutes The routes that can be embedded
 */
function addEmbedRoutes(
    sourceRoutes: PrivilegedRouteMap,
    embedRoutes: PrivilegedRouteObject[],
) {
    const routes = { ...sourceRoutes };
    embedRoutes.forEach(embedRoute => {
        const routeKey = _.findKey(
            sourceRoutes,
            route => route.path === embedRoute.path,
        );
        routes[`Embed_${routeKey}`] = {
            path: `${EMBED_ROUTE}${embedRoute.path}`,
            element: embedRoute.element,
            disableTopNav: true,
            pageContext: embedRoute.pageContext,
            isOverlayLeftPanel: embedRoute.isOverlayLeftPanel,
        };
    });

    return routes;
}

// The whitelist of embeddable routes.
// Note: Embed_Answer and Embed_SavedAnswer are defined manually above
// (AnswerLayout and SavedAnswerLayout use nested children that
// addEmbedRoutes cannot propagate).
const embedRouteList = [
    insightsRoutes.EditACopy,
    insightsRoutes.SAMLComplete,
    insightsRoutes.PinboardEmbed,
    insightsRoutes.VizEmbed,
    insightsRoutes.TabEmbed,
    insightsRoutes.SearchBarEmbed,
    insightsRoutes.EurekaPage,
    insightsRoutes.ConvAssist,
    insightsRoutes.ConvAssistAnswer,
    insightsRoutes.ConvAssistShared,
    insightsRoutes.Spotter,
];

export const INSIGHTS_ROUTES = addEmbedRoutes(insightsRoutes, embedRouteList);

export const INSIGHTS_ROUTE_IDS: any = Object.keys(INSIGHTS_ROUTES).reduce(
    (acc: any, key) => {
        acc[key] = key;
        return acc;
    },
    {},
);
